import { FC, ReactNode, useLayoutEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ReusableModal from '../common/Modal';
import { Flask, Buildings } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateAgentPhoneAccountTypeApi } from '../../utils/apis';
import { PhoneAccountTypeValue } from '../../schemas/apis/interface';
import { normalizePhoneAccountType } from '../../utils/phoneAccountType';

interface UpdatePhoneAccountTypeModalProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  currentPhoneAccountType?: string | null;
  onUpdateSuccess?: (message: string) => void;
  onUpdateError?: (message: string) => void;
}

const OPTIONS: {
  value: PhoneAccountTypeValue;
  title: string;
  description: string;
  icon: ReactNode;
}[] = [
  {
    value: 'trial',
    title: 'Trial',
    description: 'Use trial phone account settings for this agent.',
    icon: <Flask size={16} color="#6B6F80" />,
  },
  {
    value: 'prod',
    title: 'Production',
    description: 'Use production phone account settings for this agent.',
    icon: <Buildings size={16} color="#6B6F80" />,
  },
];

const UpdatePhoneAccountTypeModal: FC<UpdatePhoneAccountTypeModalProps> = ({
  open,
  onClose,
  agentId,
  currentPhoneAccountType,
  onUpdateSuccess,
  onUpdateError,
}) => {
  const normalizedCurrent = normalizePhoneAccountType(currentPhoneAccountType);
  const [selected, setSelected] = useState<PhoneAccountTypeValue>(
    () => normalizePhoneAccountType(currentPhoneAccountType) ?? 'trial'
  );
  const queryClient = useQueryClient();

  useLayoutEffect(() => {
    if (open) {
      setSelected(
        normalizePhoneAccountType(currentPhoneAccountType) ?? 'trial'
      );
    }
  }, [open, currentPhoneAccountType]);

  const { mutateAsync: updatePhoneAccountType, isPending: isUpdatePending } =
    useMutation({
      mutationFn: updateAgentPhoneAccountTypeApi,
      onSuccess: () => {
        const successMessage =
          selected === 'prod'
            ? 'Agent upgraded to Production phone account.'
            : 'Agent updated to Trial phone account.';
        onUpdateSuccess?.(successMessage);
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        onClose();
      },
      onError: (error: any) => {
        const status = error?.response?.status;
        const backendMessage = error?.response?.data?.message as
          | string
          | undefined;

        if (
          status === 400 &&
          backendMessage &&
          backendMessage.toLowerCase().includes('unassign')
        ) {
          onUpdateError?.(
            'Please unassign the phone number from this agent first, then try switching account type again.'
          );
          return;
        }

        onUpdateError?.(
          backendMessage || 'Failed to update phone account type'
        );
      },
    });

  const handleSave = async () => {
    if (normalizedCurrent !== null && selected === normalizedCurrent) {
      const unchangedMessage =
        selected === 'prod'
          ? 'This agent is already on Production. Choose Trial to switch.'
          : 'This agent is already on Trial. Choose Production to upgrade.';
      onUpdateError?.(unchangedMessage);
      return;
    }

    try {
      await updatePhoneAccountType({
        agentId,
        phoneAccountType: selected,
      });
    } catch {
      // Error surfaced via onError
    }
  };

  const optionBoxStyles = (isSelected: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    cursor: 'pointer',
    padding: '12px',
    border: '1px solid',
    borderColor: isSelected ? '#6E61F0' : '#DCE0E7',
    borderRadius: '8px',
    backgroundColor: isSelected ? 'rgba(110, 97, 240, 0.04)' : '#FFFFFF',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: isSelected
        ? 'rgba(110, 97, 240, 0.08)'
        : 'rgba(0, 0, 0, 0.04)',
      borderColor: isSelected ? '#6E61F0' : '#B0B4C1',
    },
  });

  const iconBoxStyles = (isSelected: boolean) => ({
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    backgroundColor: isSelected ? '#FFFFFF' : '#F8F9FC',
    border: '1px solid #DCE0E7',
  });

  const unchanged =
    normalizedCurrent !== null && selected === normalizedCurrent;

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Update Phone Account Type"
      width={600}
      backgroundColor="#F8F9FC"
    >
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
        <Typography
          fontSize={14}
          fontWeight={400}
          lineHeight={1.5}
          letterSpacing={-0.14}
          color="text.secondary"
          mb={3}
        >
          Choose whether this agent uses trial or production phone account
          settings.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {OPTIONS.map(opt => (
            <Box
              key={opt.value}
              onClick={() => setSelected(opt.value)}
              sx={optionBoxStyles(selected === opt.value)}
            >
              <Box sx={iconBoxStyles(selected === opt.value)}>{opt.icon}</Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    color: '#302B3F',
                    fontFamily: '"DM Sans"',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    mb: 0.5,
                  }}
                >
                  {opt.title}
                </Typography>
                <Typography
                  sx={{
                    color: '#6B6F80',
                    fontFamily: '"DM Sans"',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.12px',
                  }}
                >
                  {opt.description}
                </Typography>
              </Box>
              {selected === opt.value && (
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#6E61F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#FFFFFF',
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            pt: 2,
            borderTop: '1px solid #DCE0E7',
            mx: -2,
            px: 2,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              flex: 1,
              color: 'custom.textBlue',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={unchanged}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              flex: 1,
              backgroundColor: '#6E61F0',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#5A4FD4',
              },
              '&:disabled': {
                backgroundColor: '#B0B4C1',
              },
            }}
          >
            {isUpdatePending ? 'Updating...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </ReusableModal>
  );
};

export default UpdatePhoneAccountTypeModal;
